import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { toast } from "sonner";
import {
  Phone, MessageCircle, Star, ShieldCheck, MapPin, Plane, Hotel,
  Bus, Coffee, Compass, FileCheck2, Check, Clock, Lock, Heart,
} from "lucide-react";
import { api } from "@/lib/api";
import { trackLead } from "@/lib/analytics";
import { PACKAGE_CONFIGS } from "@/config/packages";
import { usePackageConfig } from "@/lib/packageConfig";

const WHATSAPP_NUMBER = "919501030117";

// Map icon-name strings (used in config JSON) to Lucide components.
const ICON_MAP = { Plane, Hotel, Bus, Coffee, Compass, FileCheck2, ShieldCheck, Clock, Lock, Heart };

const inr = (n) => "₹" + Number(n).toLocaleString("en-IN");

// Config-driven package landing. Renders Bali / Maldives / Leh / any new config
// from /app/frontend/src/config/packages.js with ZERO JSX edits.
export default function PackageLanding({ configKey }) {
  const { slug } = useParams();
  const key = configKey || slug || "bali";
  // usePackageConfig merges /api/packages/overrides on top of the static config
  // so admin price edits go live without a redeploy.
  const cfg = usePackageConfig(key);

  const navigate = useNavigate();
  const formRef = useRef(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [travelDate, setTravelDate] = useState("");
  const [travellers, setTravellers] = useState("2");
  const [hp, setHp] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const conversionSent = useRef(false);

  useEffect(() => { window.scrollTo(0, 0); }, [key]);

  // Social-proof "viewing now" count — deterministic per slug per 30-min bucket
  // (same number shown to all users in the same window, naturally drifts across the day).
  // Must be declared BEFORE any conditional return to respect rules-of-hooks.
  const viewingNow = useMemo(() => {
    const slugKey = cfg?.slug || key;
    const bucket = Math.floor(Date.now() / (30 * 60 * 1000));
    let h = 0;
    const s = `${slugKey}-${bucket}`;
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
    return 7 + (Math.abs(h) % 36);
  }, [cfg?.slug, key]);

  if (!cfg) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-center">
        <div>
          <h1 className="font-serif-vp text-2xl text-[var(--vp-primary)] mb-2">Package not found</h1>
          <button className="vp-btn-primary" onClick={() => navigate("/")}>Back to home</button>
        </div>
      </div>
    );
  }

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

  const submit = async (e) => {
    e?.preventDefault();
    if (hp) return;
    if (!name.trim() || !phone.trim()) { toast.error("Please share your name and phone number."); return; }
    if (phone.replace(/\D/g, "").length < 10) { toast.error("Enter a valid 10-digit phone number."); return; }
    setSubmitting(true);
    try {
      const utm = new URLSearchParams(window.location.search);
      const payload = {
        name: name.trim(),
        email: email.trim() || null,
        phone: phone.replace(/\D/g, ""),
        destination: cfg.title,
        travel_date: travelDate.trim() || null,
        persons: Number(travellers === "5+" ? 6 : travellers) || null,
        preferences: `${cfg.title} package ${inr(cfg.price)}/person · ${travellers} travellers`,
        source: `lp_${cfg.slug}`,
        utm_source: utm.get("utm_source") || null,
        utm_medium: utm.get("utm_medium") || null,
        utm_campaign: utm.get("utm_campaign") || null,
        utm_term: utm.get("utm_term") || null,
        utm_content: utm.get("utm_content") || null,
      };
      const { data: lead } = await api.post("/leads", payload);
      if (!conversionSent.current) {
        conversionSent.current = true;
        trackLead({
          source: `lp_${cfg.slug}_form`,
          destination: cfg.title,
          value: cfg.price,
          extra: { lead_id: lead?.id || null, travellers },
        });
      }
      const lines = [
        `Hi Vacations Pro — please send the ${cfg.title} package details (${inr(cfg.price)}/person).`,
        "",
        `Name: ${payload.name}`,
        `Phone: +91 ${payload.phone}`,
        `Email: ${payload.email || "—"}`,
        `Travel date: ${payload.travel_date || "—"}`,
        `Travellers: ${travellers}`,
      ];
      const wa = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(lines.join("\n"))}`;
      window.open(wa, "_blank", "noopener");
      toast.success("Got it! We've opened WhatsApp — a specialist will call within 30 minutes.");
      setName(""); setEmail(""); setPhone(""); setTravelDate("");
    } catch (err) {
      console.warn("LP submit failed", err);
      toast.error(err?.response?.data?.detail || "Couldn't submit — please WhatsApp us directly.");
    } finally { setSubmitting(false); }
  };

  const productSchema = {
    "@context": "https://schema.org", "@type": "TouristTrip",
    "name": `${cfg.title} — ${cfg.durationLong}`,
    "description": cfg.seoDesc,
    "image": cfg.heroImg,
    "provider": {
      "@type": "TravelAgency", "name": "Vacations Pro International Pvt. Ltd.",
      "telephone": "+91-9501030117", "url": "https://vacationspro.com",
      "address": {
        "@type": "PostalAddress", "streetAddress": "K-10 Tower, Feroze Gandhi Market",
        "addressLocality": "Ludhiana", "addressRegion": "Punjab",
        "postalCode": "141001", "addressCountry": "IN",
      },
    },
    "departureLocation": { "@type": "City", "name": cfg.departureCity },
    "arrivalLocation":   { "@type": "City", "name": cfg.arrivalCity },
    "offers": {
      "@type": "Offer", "price": String(cfg.price), "priceCurrency": cfg.currency,
      "availability": "https://schema.org/InStock",
      "url": `https://vacationspro.com/lp/${cfg.slug}`,
    },
    "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.8", "reviewCount": "264" },
  };
  const faqSchema = {
    "@context": "https://schema.org", "@type": "FAQPage",
    "mainEntity": cfg.faqs.map((f) => ({
      "@type": "Question", "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a },
    })),
  };

  return (
    <div className="min-h-screen bg-white text-[var(--vp-text)]" data-testid={`lp-${cfg.slug}-page`}>
      <Helmet>
        <title>{cfg.seoTitle}</title>
        <meta name="description" content={cfg.seoDesc} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={cfg.ogTitle} />
        <meta property="og:description" content={cfg.ogDesc} />
        <meta property="og:image" content={cfg.heroImg} />
        <meta property="og:type" content="product" />
        <link rel="canonical" href={`https://vacationspro.com/lp/${cfg.slug}`} />
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      {/* Sticky header */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-[var(--vp-border)] px-5 md:px-10 py-3 flex items-center justify-between">
        <button onClick={() => navigate("/")} className="flex-shrink-0" data-testid={`lp-${cfg.slug}-logo`}>
          <img src="/logo-header.png?v=blue3" alt="Vacations Pro" className="h-12 md:h-16 w-auto object-contain" />
        </button>
        <div className="flex items-center gap-2">
          <a href={`tel:+${WHATSAPP_NUMBER}`}
             onClick={() => trackLead({ source: `lp_${cfg.slug}_call_header`, destination: cfg.title })}
             className="hidden sm:inline-flex items-center gap-2 bg-[var(--vp-primary)] text-white rounded-full px-4 py-2 text-xs font-semibold">
            <Phone className="w-3.5 h-3.5" /> +91 95010 30117
          </a>
          <button onClick={scrollToForm}
                  className="bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full px-4 py-2 text-xs font-semibold transition-colors"
                  data-testid={`lp-${cfg.slug}-header-cta`}>
            Get Free Quote
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={cfg.heroImg} alt={cfg.heroAlt} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/20 to-black/70" />
        </div>
        <div className="relative z-10 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 px-6 md:px-12 lg:px-20 pt-12 md:pt-20 pb-14 md:pb-24 max-w-[1280px] mx-auto">
          <div className="text-white">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="inline-flex items-center gap-1.5 bg-white/15 backdrop-blur border border-white/20 rounded-full px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-semibold">
                <Clock className="w-3.5 h-3.5 text-[var(--vp-accent)]" /> {cfg.eyebrow}
              </div>
              <div
                className="inline-flex items-center gap-1.5 bg-emerald-500/20 backdrop-blur border border-emerald-400/40 rounded-full px-3 py-1 text-[11px] tracking-[0.15em] uppercase font-semibold text-emerald-50"
                data-testid={`lp-${cfg.slug}-viewing-now`}
              >
                <span className="relative flex w-1.5 h-1.5">
                  <span className="absolute inset-0 rounded-full bg-emerald-300 opacity-75 animate-ping" />
                  <span className="relative rounded-full w-1.5 h-1.5 bg-emerald-300" />
                </span>
                {viewingNow} viewing now
              </div>
            </div>
            <h1 className="font-serif-vp text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight mt-5 drop-shadow-[0_2px_24px_rgba(0,0,0,0.5)]">
              {cfg.title}.<br />
              <span className="text-[var(--vp-accent)]">{cfg.priceLabel}</span>
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/90 leading-relaxed max-w-[600px]">{cfg.subline}</p>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <button onClick={scrollToForm}
                className="bg-[var(--vp-accent)] hover:bg-[var(--vp-accent)]/90 text-[#0f1512] rounded-full px-7 py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-all hover:-translate-y-0.5"
                data-testid={`lp-${cfg.slug}-hero-cta`}>
                Get Free Quote
              </button>
              <div className="inline-flex items-center gap-1.5 text-[13px] text-white/85">
                <Star className="w-4 h-4 text-[var(--vp-accent)]" fill="currentColor" />
                4.8/5 on Google · 264+ reviews
              </div>
            </div>
            <div className="mt-7 grid grid-cols-4 gap-3 max-w-[480px]">
              {cfg.statStrip.map(([big, small]) => (
                <div key={small} className="bg-white/10 backdrop-blur border border-white/15 rounded-lg px-2 py-2.5 text-center">
                  <div className="font-serif-vp text-lg text-white">{big}</div>
                  <div className="text-[10px] tracking-wider uppercase text-white/65 mt-0.5">{small}</div>
                </div>
              ))}
            </div>
          </div>

          {/* FORM */}
          <form ref={formRef} onSubmit={submit}
            className="bg-white rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.35)] p-5 md:p-7 space-y-3"
            data-testid={`lp-${cfg.slug}-form`}>
            <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[var(--vp-secondary)]">
              Get your free {cfg.title.split(",")[0]} quote
            </div>
            <h2 className="font-serif-vp text-2xl md:text-3xl text-[var(--vp-primary)] leading-snug">
              Our expert calls you back in under 30 minutes.
            </h2>
            <input type="text" name="hp" tabIndex={-1} autoComplete="off" aria-hidden="true"
                   value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" />

            <BareInput label="Full Name" value={name} onChange={setName} required testid={`lp-${cfg.slug}-name`} />
            <BareInput label="Email" type="email" value={email} onChange={setEmail} testid={`lp-${cfg.slug}-email`} />
            <BareInput label="Phone (10-digit)" type="tel" required testid={`lp-${cfg.slug}-phone`}
              value={phone} onChange={(v) => setPhone(String(v).replace(/\D/g, "").slice(0, 10))} />
            <BareInput label="Travel Date" placeholder="e.g. April 12, 2026"
              value={travelDate} onChange={setTravelDate} testid={`lp-${cfg.slug}-date`} />

            <div>
              <div className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)] font-semibold mb-2">Travellers</div>
              <div className="flex flex-wrap gap-2" data-testid={`lp-${cfg.slug}-travellers`}>
                {["1", "2", "3", "4", "5+"].map((opt) => (
                  <button key={opt} type="button" onClick={() => setTravellers(opt)}
                    className={`text-xs px-3 py-2 rounded-full border transition-all ${
                      travellers === opt
                        ? "bg-[var(--vp-primary)] text-white border-[var(--vp-primary)]"
                        : "bg-white border-[var(--vp-border)] text-[var(--vp-text)] hover:border-[var(--vp-primary)]"
                    }`}>
                    {opt} {opt === "1" ? "Traveller" : "Travellers"}
                  </button>
                ))}
              </div>
            </div>

            <button type="submit" disabled={submitting}
              className="w-full bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-xl py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid={`lp-${cfg.slug}-submit`}>
              <MessageCircle className="w-4 h-4" />
              {submitting ? "Sending…" : `Get Free Quote & Save ${cfg.waSave}`}
            </button>
            <p className="text-[11px] text-[var(--vp-muted)] text-center mt-2 inline-flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" /> No spam · 100% confidential · Instant expert call-back
            </p>
          </form>
        </div>
      </section>

      {/* INCLUSIONS */}
      <section className="px-6 md:px-12 lg:px-20 py-12 max-w-[1200px] mx-auto">
        <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid={`lp-${cfg.slug}-inclusions`}>
          {cfg.inclusions.map((inc) => {
            const Icon = ICON_MAP[inc.icon] || Check;
            return (
              <div key={inc.title} className="bg-[var(--vp-bg)] border border-[var(--vp-border)] rounded-xl p-4 text-center">
                <div className="w-10 h-10 rounded-full bg-[var(--vp-primary)]/10 flex items-center justify-center mx-auto mb-3">
                  <Icon className="w-5 h-5 text-[var(--vp-primary)]" strokeWidth={1.6} />
                </div>
                <div className="text-sm font-semibold text-[var(--vp-primary)]">{inc.title}</div>
                <div className="text-[11px] text-[var(--vp-muted)] mt-0.5">{inc.body}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-[var(--vp-bg)] px-6 md:px-12 lg:px-20 py-16 border-y border-[var(--vp-border)]">
        <div className="max-w-[1200px] mx-auto">
          <span className="vp-label text-[var(--vp-secondary)]">Why Vacations Pro</span>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3 max-w-3xl">
            A trip crafted by curators, priced for everyone.
          </h2>
          <p className="text-sm text-[var(--vp-muted)] mt-4 max-w-3xl leading-relaxed">
            From our studio in Ludhiana, we've planned bespoke holidays for Indian families, couples and NRIs since 2004. Every hotel is personally vetted, every driver is English-speaking, and every itinerary balances icons with hidden gems.
          </p>
          <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {cfg.whyUs.map((w) => (
              <div key={w.title} className="bg-white border border-[var(--vp-border)] rounded-xl p-5">
                <ShieldCheck className="w-5 h-5 text-[var(--vp-secondary)] mb-3" strokeWidth={1.6} />
                <div className="font-serif-vp text-lg text-[var(--vp-primary)]">{w.title}</div>
                <p className="text-xs text-[var(--vp-muted)] leading-relaxed mt-1.5">{w.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* DAY-BY-DAY */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-[1200px] mx-auto">
        <span className="vp-label text-[var(--vp-secondary)]">{cfg.durationLong}</span>
        <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3 max-w-3xl">
          Your day-by-day {cfg.title.split(",")[0]} journey
        </h2>
        <div className="mt-10 space-y-3" data-testid={`lp-${cfg.slug}-itinerary`}>
          {cfg.days.map((d) => (
            <div key={d.n} className="bg-white border border-[var(--vp-border)] rounded-xl p-5 md:flex md:gap-6">
              <div className="md:w-32 shrink-0 mb-3 md:mb-0">
                <div className="text-[10px] tracking-[0.2em] uppercase text-[var(--vp-muted)]">Day</div>
                <div className="font-serif-vp text-4xl text-[var(--vp-secondary)]">{d.n}</div>
              </div>
              <div className="flex-1">
                <h3 className="font-serif-vp text-xl text-[var(--vp-primary)] mb-2">{d.title}</h3>
                <ul className="text-sm text-[var(--vp-text)] space-y-1.5">
                  {d.items.map((it) => (
                    <li key={it} className="flex items-start gap-2">
                      <Check className="w-4 h-4 text-[var(--vp-secondary)] mt-0.5 shrink-0" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* GALLERY */}
      <section className="bg-[var(--vp-bg)] px-6 md:px-12 lg:px-20 py-16 border-y border-[var(--vp-border)]">
        <div className="max-w-[1200px] mx-auto">
          <div className="flex items-baseline justify-between mb-6">
            <div>
              <span className="vp-label text-[var(--vp-secondary)]">Destinations</span>
              <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3">
                Moments you'll bring home
              </h2>
            </div>
            <button onClick={scrollToForm} className="hidden md:inline text-sm text-[var(--vp-secondary)] hover:underline">
              See price breakup →
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid={`lp-${cfg.slug}-gallery`}>
            {cfg.gallery.map((g) => (
              <div key={g.name}
                className={`relative rounded-xl overflow-hidden ${g.aspect === "tall" ? "row-span-2" : ""}`}
                style={{ aspectRatio: g.aspect === "tall" ? "3/5" : "1/1" }}>
                <img src={g.img} alt={`${g.name} — ${cfg.title} holiday from India`}
                     className="w-full h-full object-cover" />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                  <div className="text-white text-sm font-semibold">{g.name}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="px-6 md:px-12 lg:px-20 py-16 max-w-[1200px] mx-auto">
        <span className="vp-label text-[var(--vp-secondary)]">Traveller Stories</span>
        <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3 max-w-3xl">
          Rated 4.8/5 on Google · 264+ verified reviews
        </h2>
        <div className="mt-10 grid md:grid-cols-3 gap-4" data-testid={`lp-${cfg.slug}-reviews`}>
          {cfg.reviews.map((r) => (
            <div key={r.name} className="bg-white border border-[var(--vp-border)] rounded-xl p-6">
              <div className="flex items-center gap-1 text-[var(--vp-accent)] mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-4 h-4" fill="currentColor" strokeWidth={0} />
                ))}
              </div>
              <p className="text-sm text-[var(--vp-text)] leading-relaxed">“{r.quote}”</p>
              <div className="mt-4 flex items-center gap-3">
                <img src={r.img} alt={r.name} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <div className="text-sm font-semibold text-[var(--vp-primary)]">{r.name}</div>
                  <div className="text-[11px] text-[var(--vp-muted)]">{r.tag}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[var(--vp-bg)] px-6 md:px-12 lg:px-20 py-16 border-y border-[var(--vp-border)]">
        <div className="max-w-[820px] mx-auto">
          <span className="vp-label text-[var(--vp-secondary)]">Frequently Asked</span>
          <h2 className="font-serif-vp text-3xl md:text-4xl text-[var(--vp-primary)] leading-tight mt-3">
            Everything you want to know
          </h2>
          <div className="mt-8 divide-y divide-[var(--vp-border)] bg-white border border-[var(--vp-border)] rounded-xl"
               data-testid={`lp-${cfg.slug}-faq`}>
            {cfg.faqs.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <div key={f.q}>
                  <button onClick={() => setOpenFaq(isOpen ? null : i)}
                    className="w-full text-left px-5 py-4 flex items-center justify-between gap-4 hover:bg-[var(--vp-bg)]/50 transition-colors"
                    data-testid={`lp-${cfg.slug}-faq-${i}`} aria-expanded={isOpen}>
                    <span className="font-serif-vp text-lg text-[var(--vp-primary)] leading-snug">{f.q}</span>
                    <span className={`text-2xl text-[var(--vp-secondary)] shrink-0 transition-transform ${isOpen ? "rotate-45" : ""}`}>+</span>
                  </button>
                  {isOpen && <div className="px-5 pb-5 text-sm text-[var(--vp-text)] leading-relaxed">{f.a}</div>}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[var(--vp-primary)] text-white px-6 md:px-12 lg:px-20 py-16 text-center">
        <div className="max-w-[720px] mx-auto">
          <div className="inline-flex items-center gap-1.5 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[11px] tracking-[0.2em] uppercase font-semibold mb-5">
            <Heart className="w-3.5 h-3.5 text-[var(--vp-accent)]" fill="currentColor" /> {cfg.finalCta.badge}
          </div>
          <h2 className="font-serif-vp text-3xl md:text-5xl leading-tight">{cfg.finalCta.headline}</h2>
          <p className="text-white/75 mt-4 text-sm md:text-base">
            Flights, hotels, tours, transfers — everything covered. Talk to a specialist now and lock in the early-bird price.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-3">
            <button onClick={scrollToForm}
              className="bg-[var(--vp-accent)] text-[#0f1512] hover:bg-[var(--vp-accent)]/90 rounded-full px-7 py-3.5 text-sm tracking-[0.1em] uppercase font-bold transition-colors"
              data-testid={`lp-${cfg.slug}-final-form-cta`}>
              Get My Free Quote
            </button>
            <a href={`tel:+${WHATSAPP_NUMBER}`}
               onClick={() => trackLead({ source: `lp_${cfg.slug}_final_call`, destination: cfg.title })}
               className="bg-white/10 hover:bg-white/20 border border-white/20 rounded-full px-7 py-3.5 text-sm tracking-[0.1em] uppercase font-bold inline-flex items-center gap-2 transition-colors"
               data-testid={`lp-${cfg.slug}-final-call`}>
              <Phone className="w-4 h-4" /> Call +91 95010 30117
            </a>
          </div>
          <p className="text-[12px] text-white/60 mt-5">No credit card required · 100% confidential · Instant expert call-back</p>
        </div>
      </section>

      {/* Floating WhatsApp + mobile sticky bar */}
      <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Hello Vacations Pro — I'd like to know more about the ${cfg.title} package at ${inr(cfg.price)}.`)}`}
         target="_blank" rel="noopener noreferrer"
         onClick={() => trackLead({ source: `lp_${cfg.slug}_whatsapp_float`, destination: cfg.title })}
         className="fixed bottom-24 right-5 z-40 w-14 h-14 flex items-center justify-center bg-[#25D366] hover:bg-[#1ebe5d] text-white rounded-full shadow-[0_12px_30px_rgba(37,211,102,0.45)] transition-all hover:-translate-y-0.5"
         aria-label="Chat with us on WhatsApp" data-testid={`lp-${cfg.slug}-whatsapp-float`}>
        <MessageCircle className="w-6 h-6" />
      </a>

      <div className="fixed bottom-0 inset-x-0 z-30 md:hidden bg-white border-t border-[var(--vp-border)] shadow-[0_-8px_20px_rgba(0,0,0,0.08)] px-3 py-2.5 flex items-center gap-2">
        <a href={`tel:+${WHATSAPP_NUMBER}`}
           onClick={() => trackLead({ source: `lp_${cfg.slug}_mobile_call`, destination: cfg.title })}
           className="flex-1 inline-flex items-center justify-center gap-1.5 bg-white border border-[var(--vp-primary)] text-[var(--vp-primary)] rounded-lg py-2.5 text-xs font-bold">
          <Phone className="w-3.5 h-3.5" /> Call Now
        </a>
        <button onClick={scrollToForm}
          className="flex-[1.4] bg-[#25D366] text-white rounded-lg py-2.5 text-xs font-bold inline-flex items-center justify-center gap-1.5"
          data-testid={`lp-${cfg.slug}-mobile-form-cta`}>
          <MessageCircle className="w-3.5 h-3.5" /> Get Free Quote
        </button>
      </div>

      <footer className="px-6 md:px-12 py-8 pb-28 md:pb-8 text-center text-xs text-[var(--vp-muted)] flex items-center justify-center gap-2">
        <MapPin className="w-3.5 h-3.5" />
        K-10 Tower, Feroze Gandhi Market, Ludhiana 141001 · © {new Date().getFullYear()} Vacations Pro
      </footer>
    </div>
  );
}

function BareInput({ label, value, onChange, type = "text", placeholder = "", required = false, testid }) {
  return (
    <label className="block border border-[var(--vp-border)] rounded-lg px-3 py-2 bg-white focus-within:border-[var(--vp-primary)] transition-colors">
      <div className="text-[10px] tracking-wider uppercase text-[var(--vp-muted)] font-semibold">{label}</div>
      <input type={type} value={value} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent outline-none text-[15px] mt-0.5 placeholder:text-[var(--vp-muted)]"
        data-testid={testid} />
    </label>
  );
}
